/**
 * NEEDS ATTENTION — src/needs-attention.ts   (Metrics Layer A)
 *
 *   npx tsx scripts/test-needs-attention.ts
 *
 * The board-wide "what needs a look, and WHY" engine behind the toolbar list and the
 * per-card chip (mock 04). It does not count problems — it NAMES the cause of each
 * one, in five families, so a PM reads "on the critical path and overdue by 2 working
 * days" rather than a bare "14 need attention":
 *
 *   • behind-plan      overdue (worst when on the critical path), due after the
 *                      phase end, or planned to start before a predecessor can finish
 *   • stuck            blocked, or sat in flight longer than THIS board's own norm
 *   • hurting-schedule undated (and worse, unlinked) committed work the critical path
 *                      cannot see — straight from the CPM coverage
 *   • field-misuse     a value entered on the wrong field (misfiled.ts)
 *   • missing-basics   committed work without the owner / sprint / size to plan it.
 *                      The owner gap is a MoSCoW gap on ANY board; the sprint and
 *                      size gaps are sprint-planning discipline, so they are raised
 *                      ONLY on a board that runs on sprints (worksInSprints) — on a
 *                      Flow/builds board there are no sprints, so neither is a gap.
 *
 * RECONCILIATION (the binding contract with the mock): every card the epics already
 * count as "needs attention" — `epic-rollup.needsAttention`, i.e. an unresolved card
 * that is blocked OR overdue — appears in THIS list. The board view is a strict
 * SUPERSET: it keeps those cards and adds the data-quality and schedule causes the
 * epic figure never covered. `reconciledRefs` is exactly that subset, so a test can
 * prove the superset holds and an epic reading "2 need attention" always points at
 * two rows a person can find here.
 *
 * Pure: it reuses the shipped signals (reallyBlocked, card-age's board-norm aging,
 * cardDueState, misfiledAs, the CPM result) rather than re-deriving any of them, so
 * its pace/stale/overdue maths can never disagree with the forecast, the milestones
 * or the Timeline. Proved by scripts/test-needs-attention.ts.
 */
import type { Issue, IssueStatus } from './types';
import { isInProcess } from './types';
import type { Project } from './projects';
import { predecessorsOf, type DepCard } from './dependencies';
import { worksInSprints } from './sprints';
import { reallyBlocked } from './blocked';
import { agingOf, ageInDays } from './card-age';
import { cardDueState, type Phase } from './phases';
import { misfiledAs } from './misfiled';
import type { CpmResult } from './critical-path';
import { workingDaysBetween, DEFAULT_CALENDAR, type WorkCalendar } from './work-calendar';

export type AttnCategory = 'behind-plan' | 'stuck' | 'hurting-schedule' | 'field-misuse' | 'missing-basics';

/** Red is reserved for the genuine RAG alarm — overdue and blocked. Everything else
 *  that warrants a look is amber (the brand petrol/amber warning), never red. */
export type AttnSeverity = 'red' | 'amber';

export type AttnCause =
  | 'critical-overdue'
  | 'overdue'
  | 'due-after-phase'
  | 'start-at-risk'
  | 'stuck-blocked'
  | 'stuck-aged'
  | 'undated-unlinked'
  | 'undated'
  | 'misfiled-epic'
  | 'misfiled-area'
  | 'no-owner'
  | 'no-sprint'
  | 'no-size';

/** The one-click action a row offers — the UI maps it to the real control. */
export type AttnFix = 'open-card' | 'set-dates' | 'assign' | 'add-to-sprint' | 'set-size' | 'move-to-status' | 'review';

/** The structured facts a row's sentence is composed from — kept as data so the
 *  copy lives in the UI, never baked into the engine (and so a test asserts numbers,
 *  not prose). Only the fields a given cause needs are set. */
export interface AttnFacts {
  overdueWorkingDays?: number;
  dueDate?: string;
  phaseName?: string;
  phaseEnd?: string;
  blockedDays?: number | null;
  /** The board's own median blocker-clearance in calendar days (null when the
   *  board has no cleared-block history yet to compare against). */
  typicalClearDays?: number | null;
  /** True only when this card has been blocked longer than that board median —
   *  so the sentence claims "longer than usual" only when it IS. */
  longerThanTypical?: boolean;
  agedDays?: number;
  misfiledValue?: string;
  looksLike?: string;
  /** For the undated causes: which of the two planning dates the card is missing,
   *  so the chip and sentence name the real gap (a card with a due but no start is
   *  "No start date", not "No dates set"). */
  missingStart?: boolean;
  missingDue?: boolean;
}

export interface AttentionItem {
  id: string;
  ref: string;
  title: string;
  category: AttnCategory;
  cause: AttnCause;
  severity: AttnSeverity;
  onCriticalPath: boolean;
  /** The short label for the card-face chip — the one `.rag` badge (R3). */
  chip: string;
  facts: AttnFacts;
  fix: AttnFix;
}

export interface NeedsAttentionResult {
  /** Every item, ranked most-severe first. */
  items: AttentionItem[];
  /** The items split by cause family, each in rank order. */
  byCategory: Record<AttnCategory, AttentionItem[]>;
  /** Per-family counts plus the grand total. */
  counts: Record<AttnCategory, number> & { total: number };
  /** The refs of the blocked/overdue cards the epics already count — the subset this
   *  list is a superset of. Every ref here has at least one item above. */
  reconciledRefs: string[];
}

export interface NeedsAttentionInput {
  issues: Issue[];
  phases: Phase[];
  /** The critical path over the board's open, dated cards (see critical-path.ts). */
  cpm: CpmResult;
  now: number;
  cal?: WorkCalendar;
  /** The board's real issue-type labels, so a legit type value is not read as
   *  misfiled. Optional — defaults to none. */
  issueTypes?: string[];
  /** The board's planning cadence. The sprint and size gaps below are raised only
   *  when this is 'sprints' (worksInSprints). Undefined → a Flow/builds board, which
   *  has no sprints, so neither gap applies. */
  cadence?: Project['cadence'];
}

const CATEGORY_ORDER: AttnCategory[] = ['behind-plan', 'stuck', 'hurting-schedule', 'field-misuse', 'missing-basics'];

const isResolved = (s: IssueStatus): boolean => s === 'done' || s === 'wontdo';
const hasText = (v: string | null | undefined): v is string => typeof v === 'string' && v.trim().length > 0;

/** The middle value of a non-empty list (even length → rounded mean of the two). */
function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : Math.round((s[mid - 1] + s[mid]) / 2);
}

/** A `Date` ms at local midnight for a `YYYY-MM-DD` — matches work-calendar's own
 *  day handling (no UTC drift), for the overdue-working-days count. */
function localMs(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1).getTime();
}

/** The Issue as the dependency graph reads it — just the fields predecessorsOf needs. */
function asDepCard(i: Issue): DepCard {
  return { id: i.id, ref: i.ref, status: i.status, startsOn: i.startsOn, dueDate: i.dueDate, blockerRefs: i.blockerRefs };
}

/**
 * Classify the whole board. Returns every flagged card with its cause, ranked, split
 * by family, counted, and the reconciliation subset.
 */
export function needsAttention(input: NeedsAttentionInput): NeedsAttentionResult {
  const { issues, phases, cpm, now } = input;
  const cal = input.cal ?? DEFAULT_CALENDAR;
  const issueTypes = input.issueTypes ?? [];
  // The sprint and size gaps are sprint-planning discipline — a committed must-have
  // nobody has scheduled into a fortnight, or a sprint card with no estimate. On a
  // board that does not run on sprints there are no fortnights to miss, so neither is
  // a gap; the SAME helper every other sprint surface reads, so this can't drift.
  const inSprints = worksInSprints({ cadence: input.cadence });
  const items: AttentionItem[] = [];

  // The process starts at To-Do: the backlog is a dumping ground of ideas, not yet
  // owed a plan, so NO attention cause is raised on a backlog card (Vinnie, 3 Nov).
  // Only the backlog-growth insight counts the backlog itself. Resolved cards (done/
  // wontdo) are out as before. `isInProcess` is the single rule every metric shares.
  const open = issues.filter((i) => isInProcess(i.status));
  const phaseById = new Map(phases.map((p) => [p.id, p] as const));
  const blockedSet = new Set(reallyBlocked(issues).map((i) => i.ref));

  // The board's OWN typical blocker clearance: the median calendar-days a block
  // that was actually resolved lasted (blockedAt → unblockedAt). A currently-
  // blocked card is compared against this, so "longer than usual" is only ever
  // claimed when it is true. Null when the board has no cleared block to learn
  // from yet — then no such claim is made. Measured the SAME way blockedDays is
  // (calendar days from ageInDays), so the comparison is like-for-like.
  const clearedDurations = issues
    .map((i) =>
      i.blockedAt != null && i.unblockedAt != null && i.unblockedAt > i.blockedAt
        ? Math.round((i.unblockedAt - i.blockedAt) / 86_400_000)
        : null,
    )
    .filter((d): d is number => d != null);
  const typicalClearDays = clearedDurations.length ? median(clearedDurations) : null;
  const onCrit = (ref: string): boolean => cpm.nodes.get(ref)?.critical ?? false;
  const undated = new Set(cpm.coverage.undatedRefs);

  const base = (i: Issue) => ({ id: i.id, ref: i.ref, title: i.title, onCriticalPath: onCrit(i.ref) });

  for (const i of open) {
    const dueState = cardDueState(i.dueDate, i.status, now);
    const isMust = i.moscow === 'must';

    // ── BEHIND PLAN ────────────────────────────────────────────────────────────
    if (dueState === 'overdue' && hasText(i.dueDate)) {
      const overdueWorkingDays = workingDaysBetween(localMs(i.dueDate), now, cal);
      const critical = onCrit(i.ref);
      items.push({
        ...base(i),
        category: 'behind-plan',
        cause: critical ? 'critical-overdue' : 'overdue',
        severity: 'red',
        chip: critical ? 'Overdue · critical path' : 'Overdue',
        facts: { overdueWorkingDays, dueDate: i.dueDate },
        fix: 'open-card',
      });
    } else if (hasText(i.dueDate) && i.phase && phaseById.get(i.phase)?.targetEnd && i.dueDate > phaseById.get(i.phase)!.targetEnd!) {
      // Due after its phase's end — a plan that cannot hold even if nothing slips.
      const ph = phaseById.get(i.phase)!;
      items.push({
        ...base(i),
        category: 'behind-plan',
        cause: 'due-after-phase',
        severity: 'amber',
        chip: 'Due after phase end',
        facts: { dueDate: i.dueDate, phaseName: ph.name, phaseEnd: ph.targetEnd! },
        fix: 'open-card',
      });
    } else if (hasText(i.startsOn)) {
      // At risk of starting late: a predecessor cannot finish before this card's
      // planned start — either the predecessor is overdue, or its own planned due is
      // on/after this card's planned start.
      const preds = predecessorsOf(asDepCard(i));
      const atRisk = preds.some((pr) => {
        const p = issues.find((x) => x.ref === pr);
        if (!p || isResolved(p.status)) return false;
        if (cardDueState(p.dueDate, p.status, now) === 'overdue') return true;
        return hasText(p.dueDate) && p.dueDate >= i.startsOn!;
      });
      if (atRisk) {
        items.push({
          ...base(i),
          category: 'behind-plan',
          cause: 'start-at-risk',
          severity: 'amber',
          chip: 'At risk of starting late',
          facts: {},
          fix: 'open-card',
        });
      }
    }

    // ── STUCK ──────────────────────────────────────────────────────────────────
    if (blockedSet.has(i.ref)) {
      const blockedDays = ageInDays(i.blockedAt, now);
      // Claim "longer than usual" only against a MEANINGFUL baseline: a board
      // whose cleared blocks all resolved within a day (median rounds to 0) has
      // no real norm to beat, and "(about 0 days)" would read as nonsense — such
      // a card just states how long it has been blocked, with no comparison.
      const longerThanTypical =
        typicalClearDays != null && typicalClearDays >= 1 && blockedDays != null && blockedDays > typicalClearDays;
      items.push({
        ...base(i),
        category: 'stuck',
        cause: 'stuck-blocked',
        severity: 'red',
        chip: 'Blocked',
        facts: { blockedDays, typicalClearDays, longerThanTypical },
        fix: 'open-card',
      });
    } else {
      // Not blocked, but sat in flight longer than this board's own norm (card-age).
      const age = agingOf(i, issues, now);
      if (age?.aged) {
        items.push({
          ...base(i),
          category: 'stuck',
          cause: 'stuck-aged',
          severity: 'amber',
          chip: `In flight ${age.days}d`,
          facts: { agedDays: age.days },
          fix: 'open-card',
        });
      }
    }

    // ── HURTING THE SCHEDULE ─────────────────────────────────────────────────────
    // In-process work the critical path cannot place because it has no dates. (The
    // backlog is already excluded upstream — a parked idea is not yet owed dates.)
    if (undated.has(i.ref)) {
      const linked = predecessorsOf(asDepCard(i)).length > 0;
      // Name the real gap: a card with a due but no start is missing a START, not
      // "all dates" — the CPM drops it either way, but the chip must be honest.
      const missingStart = !hasText(i.startsOn);
      const missingDue = !hasText(i.dueDate);
      const chip = missingStart && missingDue ? 'No dates set' : missingStart ? 'No start date' : 'No due date';
      items.push({
        ...base(i),
        category: 'hurting-schedule',
        cause: linked ? 'undated' : 'undated-unlinked',
        severity: 'amber',
        chip,
        facts: { missingStart, missingDue },
        fix: 'set-dates',
      });
    }

    // ── FIELD MISUSE ─────────────────────────────────────────────────────────────
    if (hasText(i.epic)) {
      const mis = misfiledAs(i.epic, issueTypes);
      if (mis) {
        items.push({
          ...base(i),
          category: 'field-misuse',
          cause: 'misfiled-epic',
          severity: 'amber',
          chip: 'Check the Epic field',
          facts: { misfiledValue: i.epic, looksLike: mis.field },
          fix: mis.field === 'status' ? 'move-to-status' : 'review',
        });
      }
    }
    if (hasText(i.area)) {
      const mis = misfiledAs(i.area, issueTypes);
      if (mis) {
        items.push({
          ...base(i),
          category: 'field-misuse',
          cause: 'misfiled-area',
          severity: 'amber',
          chip: 'Check the Area field',
          facts: { misfiledValue: i.area, looksLike: mis.field },
          fix: 'review',
        });
      }
    }

    // ── MISSING THE BASICS ───────────────────────────────────────────────────────
    // Committed work (a must-have, or a card put in a sprint) without what it takes
    // to plan it. (Backlog is already excluded upstream — a blank there is a park, not
    // a gap.) The owner gap is a MoSCoW gap on ANY board. The sprint and size gaps are
    // sprint-planning discipline, so they are raised only when the board runs on
    // sprints — on a Flow/builds board there are no sprints (and a card carrying a
    // stale sprint id from a since-switched board must not be nagged either).
    const committed = isMust || hasText(i.sprint);
    if (committed) {
      if (isMust && !hasText(i.assignee)) {
        items.push({ ...base(i), category: 'missing-basics', cause: 'no-owner', severity: 'amber', chip: 'No owner', facts: {}, fix: 'assign' });
      }
      if (inSprints && isMust && !hasText(i.sprint)) {
        items.push({ ...base(i), category: 'missing-basics', cause: 'no-sprint', severity: 'amber', chip: 'No sprint', facts: {}, fix: 'add-to-sprint' });
      }
      if (inSprints && hasText(i.sprint) && !hasText(i.size)) {
        items.push({ ...base(i), category: 'missing-basics', cause: 'no-size', severity: 'amber', chip: 'No size', facts: {}, fix: 'set-size' });
      }
    }
  }

  // RANK — family order, then red before amber, then by numeric ref so the list is
  // stable and the worst thing is always on top.
  const sevRank = (s: AttnSeverity) => (s === 'red' ? 0 : 1);
  const refNum = (ref: string) => {
    const m = ref.match(/(\d+)/);
    return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
  };
  items.sort((a, b) => {
    const c = CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category);
    if (c !== 0) return c;
    const s = sevRank(a.severity) - sevRank(b.severity);
    if (s !== 0) return s;
    return refNum(a.ref) - refNum(b.ref);
  });

  const byCategory = {
    'behind-plan': [], stuck: [], 'hurting-schedule': [], 'field-misuse': [], 'missing-basics': [],
  } as Record<AttnCategory, AttentionItem[]>;
  for (const it of items) byCategory[it.category].push(it);

  const counts = {
    'behind-plan': byCategory['behind-plan'].length,
    stuck: byCategory.stuck.length,
    'hurting-schedule': byCategory['hurting-schedule'].length,
    'field-misuse': byCategory['field-misuse'].length,
    'missing-basics': byCategory['missing-basics'].length,
    total: items.length,
  };

  // RECONCILIATION — the blocked/overdue open cards the epics count. Every one must
  // have an item above; the test asserts it.
  const flaggedRefs = new Set(items.map((it) => it.ref));
  const reconciledRefs = open
    .filter((i) => blockedSet.has(i.ref) || cardDueState(i.dueDate, i.status, now) === 'overdue')
    .map((i) => i.ref)
    .filter((ref) => flaggedRefs.has(ref));

  return { items, byCategory, counts, reconciledRefs };
}

/** The one card-face chip for a card, if it needs attention — its single WORST item,
 *  so the tile shows one `.rag` badge, never a stack (R3). Worst means most severe
 *  first (red overdue/blocked beats an amber warning), then the family order, so a
 *  card that is both blocked and at-risk wears the red "Blocked", not the amber note.
 *  Null when the card is clear. */
export function attentionChip(
  result: NeedsAttentionResult,
  ref: string,
): { chip: string; severity: AttnSeverity; cause: AttnCause } | null {
  const mine = result.items.filter((it) => it.ref === ref);
  if (mine.length === 0) return null;
  const sevRank = (s: AttnSeverity) => (s === 'red' ? 0 : 1);
  mine.sort((a, b) => sevRank(a.severity) - sevRank(b.severity) || CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category));
  const w = mine[0];
  return { chip: w.chip, severity: w.severity, cause: w.cause };
}
